export const hasWorkOsConfig = Boolean(import.meta.env.VITE_WORKOS_CLIENT_ID as string | undefined);
export const hasConvexConfig = Boolean(import.meta.env.VITE_CONVEX_URL as string | undefined);

export function getConvexUrl(): string | undefined {
  return import.meta.env.VITE_CONVEX_URL as string | undefined;
}

export function getWorkOsClientId(): string | undefined {
  return import.meta.env.VITE_WORKOS_CLIENT_ID as string | undefined;
}

export function getWorkOsApiHostname(): string | undefined {
  const value = (import.meta.env.VITE_WORKOS_API_HOSTNAME as string | undefined)?.trim();
  return value ? value : undefined;
}

export function getConfiguredWorkOsRedirectUri(): string | undefined {
  const value = (import.meta.env.VITE_WORKOS_REDIRECT_URI as string | undefined)?.trim();
  return value ? value : undefined;
}

/** True for browser loopback hosts used in local Vite development. */
export function isLoopbackHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "::1"
  );
}

/**
 * Resolve the AuthKit redirect URI so the PKCE code_verifier origin matches the
 * redirect target. Loopback hosts (localhost ↔ 127.0.0.1) may rewrite to the
 * current origin; production never rewrites to a different public origin.
 */
export function resolveWorkOsRedirectUri(
  configured: string | undefined,
  currentOrigin: string | undefined,
): string | undefined {
  if (!configured) {
    return currentOrigin ? `${currentOrigin}/callback` : undefined;
  }

  if (!currentOrigin) {
    return configured;
  }

  let configuredUrl: URL;
  let currentUrl: URL;
  try {
    configuredUrl = new URL(configured);
    currentUrl = new URL(currentOrigin);
  } catch {
    return configured;
  }

  if (configuredUrl.origin === currentUrl.origin) {
    return configured;
  }

  if (isLoopbackHostname(configuredUrl.hostname) && isLoopbackHostname(currentUrl.hostname)) {
    return `${currentUrl.origin}${configuredUrl.pathname}${configuredUrl.search}${configuredUrl.hash}`;
  }

  return configured;
}

export function getWorkOsRedirectUri(): string | undefined {
  return resolveWorkOsRedirectUri(
    getConfiguredWorkOsRedirectUri(),
    typeof window !== "undefined" ? window.location.origin : undefined,
  );
}

export type AuthKitSessionMode =
  | {
      /** Custom Authentication API domain present — use secure HttpOnly cookie sessions. */
      kind: "cookie";
      apiHostname: string;
      devMode: false;
    }
  | {
      /**
       * No custom Authentication API domain — WorkOS-documented fallback stores the
       * refresh token in localStorage (XSS-accessible). Prefer configuring
       * VITE_WORKOS_API_HOSTNAME in production when a custom domain is available.
       */
      kind: "devMode";
      apiHostname?: undefined;
      devMode: true;
    };

/**
 * AuthKit cookie sessions require a first-party custom Authentication API domain.
 * Without one, browsers block the third-party api.workos.com session cookie and
 * `authenticateWithRefreshToken` returns HTTP 400. WorkOS documents setting
 * `devMode={true}` in that case.
 */
export function resolveAuthKitSessionMode(apiHostname: string | undefined): AuthKitSessionMode {
  if (apiHostname) {
    return { kind: "cookie", apiHostname, devMode: false };
  }
  return { kind: "devMode", devMode: true };
}

export function getAuthKitSessionMode(): AuthKitSessionMode {
  return resolveAuthKitSessionMode(getWorkOsApiHostname());
}

/**
 * PKCE requires sign-in to start on the same origin as the redirect URI.
 * Returns the absolute URL to navigate to when the current origin differs
 * (non-loopback mismatches only — loopback is rewritten in resolveWorkOsRedirectUri).
 */
export function getSignInOriginCorrectionUrl(
  redirectUri: string | undefined,
  currentHref: string | undefined,
): string | undefined {
  if (!redirectUri || !currentHref) return undefined;

  let redirectUrl: URL;
  let currentUrl: URL;
  try {
    redirectUrl = new URL(redirectUri);
    currentUrl = new URL(currentHref);
  } catch {
    return undefined;
  }

  if (redirectUrl.origin === currentUrl.origin) {
    return undefined;
  }

  // Loopback rewrite is handled by resolveWorkOsRedirectUri; do not bounce the tab.
  if (isLoopbackHostname(redirectUrl.hostname) && isLoopbackHostname(currentUrl.hostname)) {
    return undefined;
  }

  return `${redirectUrl.origin}${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
}
